import {
  handleFetchResponse,
  getFormURLEncodedBody,
} from '@stripe-internal/connect-embedded-lib';
import {getDevLogger} from '../utils/getLogger';
import type {RiskSignalsCollectionDataLayerResponse} from './types';
import type {
  IFrameCollectRiskSignalsRequest,
  IFrameCollectRiskSignalsResponse,
} from '../data-layer-client/FrameMessenger';
import {authStore} from './authentication/AuthenticationStore';
import {getCurrentScriptUrlContext} from '../utils/getCurrentScriptUrlContext';
import {getStripeApiHost} from '../data-layer-client/buildStripeClient';
import {getDataLayerHeaders} from './proxyRequest';

const devLogger = getDevLogger();

/**
 * Collect and send risk signals to Stripe via the /v1/risk_signals endpoint
 * @param request {IFrameCollectRiskSignalsRequest} The data layer request
 * @returns {IFrameCollectRiskSignalsResponse} The data layer response
 */
export async function collectAndSendRiskSignals(
  request: IFrameCollectRiskSignalsRequest,
): Promise<IFrameCollectRiskSignalsResponse> {
  devLogger.info('Data Layer: collectAndSendRiskSignals called');

  try {
    // Send risk signals to Stripe
    await sendRiskSignalsToStripe();

    // Build data layer response
    const response: RiskSignalsCollectionDataLayerResponse = {
      status: 'completed',
    };

    return {
      id: request.id,
      type: request.type,
      requestType: 'collectRiskSignalsResponse',
      response,
    };
  } catch (error) {
    devLogger.error('Error in collectAndSendRiskSignals:', error);

    // TODO (@justinrao: 2025-12-31) extend to implement the full error payload
    return {
      id: request.id,
      type: request.type,
      requestType: 'collectRiskSignalsResponse',
      response: {
        status: 'failed',
      },
    };
  }
}

/**
 * Send risk signals to Stripe via the /v1/risk_signals endpoint
 * @returns The risk signals response from Stripe
 */
async function sendRiskSignalsToStripe() {
  // Get API host
  const {serviceEnvironment} = getCurrentScriptUrlContext();
  const apiHost = getStripeApiHost(serviceEnvironment);

  // Make request to /v1/accounts endpoint
  devLogger.info('Posting risk signals to /v1/risk_signals', {
    apiHost,
  });

  // Get API key
  const apiKey = authStore.getApiKey('account_session');
  if (!apiKey) {
    throw new Error('No valid account session apiKey found');
  }

  // Build headers
  const headers: HeadersInit = getDataLayerHeaders({
    Authorization: `Bearer ${apiKey.key}`,
    'Content-Type': 'application/x-www-form-urlencoded',
  });

  const riskSignalsUrl = `https://${apiHost}/v1/risk_signals`;
  const fetchResponse = await fetch(riskSignalsUrl, {
    method: 'POST',
    headers,
    body: getFormURLEncodedBody({
      // TODO (@justinrao: 2025-12-31) add hcaptcha and iovation data
    }),
  });

  return handleFetchResponse(fetchResponse);
}
